import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboardingStore } from '../store/onboardingStore';
import { useAuthStore } from '../store/authStore';
import { useThemeStore } from '../store/themeStore';
import { useWorkHoursStore } from '../store/workHoursStore';
import { Button, Card } from '../components/ui';
import { CSVImportModal } from '../components/contacts';

export default function Debug() {
  const navigate = useNavigate();
  const { reset: resetOnboarding } = useOnboardingStore();
  const { token } = useAuthStore();
  const { setTheme } = useThemeStore();
  const { setWorkHours } = useWorkHoursStore();

  // Core tools state
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [resetResult, setResetResult] = useState<string | null>(null);
  const [showCSVImport, setShowCSVImport] = useState(false);

  // Toggle sections
  const [showPrefsReset, setShowPrefsReset] = useState(false);
  const [showApiDebug, setShowApiDebug] = useState(false);

  // API debug state
  const [apiEndpoint, setApiEndpoint] = useState('/me');
  const [apiResponse, setApiResponse] = useState<string | null>(null);
  const [apiLoading, setApiLoading] = useState(false);

  const apiUrl = import.meta.env.VITE_API_URL;

  // ============================================
  // Core Tools
  // ============================================

  const handleDeleteAllContacts = async () => {
    if (!confirm('Are you sure you want to DELETE ALL CONTACTS? This cannot be undone!')) {
      return;
    }

    setIsDeleting(true);
    setDeleteResult(null);

    try {
      const response = await fetch(`${apiUrl}/contacts/all`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setDeleteResult(`Deleted ${data.data.deleted_count} contacts`);
      } else {
        setDeleteResult(`Error: ${data.error?.message || 'Unknown error'}`);
      }
    } catch (err) {
      setDeleteResult(`Error: ${err instanceof Error ? err.message : 'Network error'}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleFullAccountReset = async () => {
    if (!confirm('FULL ACCOUNT RESET: This will delete ALL contacts AND all interactions. This cannot be undone!')) {
      return;
    }

    setIsResetting(true);
    setResetResult(null);

    try {
      const response = await fetch(`${apiUrl}/contacts/debug/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.success) {
        setResetResult(`Reset complete: ${data.data.contacts_deleted} contacts, ${data.data.interactions_deleted} interactions deleted`);
      } else {
        setResetResult(`Error: ${data.error?.message || 'Unknown error'}`);
      }
    } catch (err) {
      setResetResult(`Error: ${err instanceof Error ? err.message : 'Network error'}`);
    } finally {
      setIsResetting(false);
    }
  };

  const handleRestartOnboarding = () => {
    resetOnboarding();
    navigate('/dashboard');
  };

  const handleClearLocalStorage = () => {
    if (!confirm('Clear all local storage? You will be logged out.')) {
      return;
    }
    localStorage.clear();
    window.location.href = '/login';
  };

  // ============================================
  // Preferences Reset
  // ============================================

  const handleResetWorkHours = () => {
    setWorkHours({
      startHour: 9,
      startMinute: 0,
      endHour: 17,
      endMinute: 0,
      workDays: [1, 2, 3, 4, 5], // Mon-Fri
    });
    alert('Work hours reset to defaults (9 AM - 5 PM, Mon-Fri)');
  };

  const handleResetTheme = () => {
    setTheme('system');
    alert('Theme reset to system default');
  };

  const handleResetAIPrefs = () => {
    localStorage.removeItem('ondeck-ai-preferences');
    alert('AI preferences reset');
  };

  const handleResetNotifications = () => {
    localStorage.removeItem('ondeck-notifications');
    alert('Notification preferences reset');
  };

  const handleResetAllPrefs = () => {
    if (!confirm('Reset ALL preferences to defaults?')) return;
    handleResetWorkHours();
    handleResetTheme();
    handleResetAIPrefs();
    handleResetNotifications();
  };

  // ============================================
  // API Debug Tools
  // ============================================

  const handleTestEndpoint = async () => {
    setApiLoading(true);
    setApiResponse(null);

    try {
      const response = await fetch(`${apiUrl}${apiEndpoint}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      setApiResponse(JSON.stringify(data, null, 2));
    } catch (err) {
      setApiResponse(`Error: ${err instanceof Error ? err.message : 'Network error'}`);
    } finally {
      setApiLoading(false);
    }
  };

  const handleCopyCurl = () => {
    const curl = `curl -X GET "${apiUrl}${apiEndpoint}" \\\n  -H "Authorization: Bearer ${token}"`;
    navigator.clipboard.writeText(curl);
    alert('Curl command copied to clipboard');
  };

  const maskedToken = token ? `${token.slice(0, 20)}...${token.slice(-10)}` : 'No token';

  return (
    <div className="min-h-screen bg-red-700 py-8">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white tracking-wide">DEBUG</h1>
          <p className="text-red-200 text-sm mt-1">Development Tools - Not for regular use</p>
        </div>

        <div className="space-y-6">
        {/* ============================================ */}
        {/* Core Tools */}
        {/* ============================================ */}

        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Core Tools
            </h2>

            <div className="space-y-4">
              {/* Delete All Contacts */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Delete All Contacts</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Permanently delete all your contacts and interactions
                  </p>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleDeleteAllContacts}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete All'}
                </Button>
              </div>
              {deleteResult && (
                <p className={`text-sm ${deleteResult.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {deleteResult}
                </p>
              )}

              {/* Full Account Reset */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Full Account Reset</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Delete all contacts AND all interactions - complete data reset
                  </p>
                </div>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleFullAccountReset}
                  disabled={isResetting}
                >
                  {isResetting ? 'Resetting...' : 'Reset All Data'}
                </Button>
              </div>
              {resetResult && (
                <p className={`text-sm ${resetResult.startsWith('Error') ? 'text-red-600' : 'text-green-600'}`}>
                  {resetResult}
                </p>
              )}

              {/* Restart Onboarding */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Restart Onboarding</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Reset and go through the welcome flow again
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={handleRestartOnboarding}>
                  Restart
                </Button>
              </div>

              {/* Import CSV */}
              <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Import CSV</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Import contacts from CSV with AI field mapping
                  </p>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowCSVImport(true)}>
                  Import
                </Button>
              </div>

              {/* Clear Local Storage */}
              <div className="flex items-center justify-between py-3">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Clear Local Storage</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Clear all local data and log out
                  </p>
                </div>
                <Button variant="secondary" size="sm" onClick={handleClearLocalStorage}>
                  Clear & Logout
                </Button>
              </div>
            </div>
          </div>
        </Card>

        {/* ============================================ */}
        {/* Preferences Reset (Collapsible) */}
        {/* ============================================ */}

        <Card>
          <div className="p-6">
            <button
              onClick={() => setShowPrefsReset(!showPrefsReset)}
              className="w-full flex items-center justify-between"
            >
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Preferences Reset
              </h2>
              <span className="text-gray-500">{showPrefsReset ? '▼' : '▶'}</span>
            </button>

            {showPrefsReset && (
              <div className="mt-4 space-y-4">
                <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Work Hours</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Reset to 9 AM - 5 PM, Mon-Fri</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleResetWorkHours}>Reset</Button>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Theme</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Reset to system default</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleResetTheme}>Reset</Button>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">AI Preferences</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Reset AI feature toggles</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleResetAIPrefs}>Reset</Button>
                </div>

                <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Notifications</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Reset notification settings</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={handleResetNotifications}>Reset</Button>
                </div>

                <div className="pt-2">
                  <Button variant="secondary" size="sm" onClick={handleResetAllPrefs}>
                    Reset All Preferences
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* ============================================ */}
        {/* API Debug Tools (Collapsible) */}
        {/* ============================================ */}

        <Card>
          <div className="p-6">
            <button
              onClick={() => setShowApiDebug(!showApiDebug)}
              className="w-full flex items-center justify-between"
            >
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                API Debug Tools
              </h2>
              <span className="text-gray-500">{showApiDebug ? '▼' : '▶'}</span>
            </button>

            {showApiDebug && (
              <div className="mt-4 space-y-4">
                {/* Token Display */}
                <div className="py-3 border-b border-gray-100 dark:border-gray-700">
                  <p className="font-medium text-gray-900 dark:text-white mb-2">Auth Token</p>
                  <code className="block p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono break-all">
                    {maskedToken}
                  </code>
                </div>

                {/* API URL */}
                <div className="py-3 border-b border-gray-100 dark:border-gray-700">
                  <p className="font-medium text-gray-900 dark:text-white mb-2">API URL</p>
                  <code className="block p-2 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono">
                    {apiUrl}
                  </code>
                </div>

                {/* Test Endpoint */}
                <div className="py-3 border-b border-gray-100 dark:border-gray-700">
                  <p className="font-medium text-gray-900 dark:text-white mb-2">Test Endpoint</p>
                  <div className="flex gap-2">
                    <select
                      value={apiEndpoint}
                      onChange={(e) => setApiEndpoint(e.target.value)}
                      className="flex-1 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-sm"
                    >
                      <option value="/me">GET /me</option>
                      <option value="/contacts">GET /contacts</option>
                      <option value="/contacts?limit=5">GET /contacts?limit=5</option>
                    </select>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleTestEndpoint}
                      disabled={apiLoading}
                    >
                      {apiLoading ? 'Loading...' : 'Test'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handleCopyCurl}>
                      Copy Curl
                    </Button>
                  </div>
                </div>

                {/* Response */}
                {apiResponse && (
                  <div className="py-3">
                    <p className="font-medium text-gray-900 dark:text-white mb-2">Response</p>
                    <pre className="p-3 bg-gray-100 dark:bg-gray-700 rounded text-xs font-mono overflow-x-auto max-h-64 overflow-y-auto">
                      {apiResponse}
                    </pre>
                  </div>
                )}
              </div>
            )}
          </div>
        </Card>

        {/* Quick Navigation */}
        <Card>
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Quick Links
            </h2>
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={() => navigate('/contacts')}>
                Contacts
              </Button>
              <Button variant="secondary" size="sm" onClick={() => navigate('/settings')}>
                Settings
              </Button>
              <Button variant="secondary" size="sm" onClick={() => navigate('/tags')}>
                Tags
              </Button>
              <Button variant="secondary" size="sm" onClick={() => navigate('/dashboard')}>
                Dashboard
              </Button>
            </div>
          </div>
        </Card>
      </div>

        {/* CSV Import Modal */}
        <CSVImportModal
          open={showCSVImport}
          onClose={() => setShowCSVImport(false)}
          onImportComplete={() => {
            setShowCSVImport(false);
            navigate('/contacts');
          }}
        />
      </div>
    </div>
  );
}
